import React, { useState, useEffect, useContext } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  Box, 
  CircularProgress,
  Stack,
  Typography,
  Chip,
  OutlinedInput,
  TextField,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { AuthContext } from '../../context/AuthContext';
import { fetchClientes, fetchTiendasPorCliente } from './vigilanteService';
import './VigilanteFilters.css';

/**
 * Componente para filtrar vigilantes con selección múltiple
 * @param {Object} props - Propiedades del componente
 * @param {Function} props.onFilterApply - Función que se ejecuta al aplicar los filtros
 * @param {boolean} props.isLoading - Estado de carga
 * @returns {JSX.Element}
 */
const VigilanteFilters = ({ onFilterApply, isLoading }) => {
  // Estados para los filtros
  const [clienteFilter, setClienteFilter] = useState([]);
  const [servicioFilter, setServicioFilter] = useState([]);
  
  // Estados para búsqueda
  const [clienteSearch, setClienteSearch] = useState('');
  const [servicioSearch, setServicioSearch] = useState('');
  
  // Estados para las opciones de los dropdowns
  const [clientes, setClientes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [todosServicios, setTodosServicios] = useState([]);
  
  // Estado para la carga de las opciones
  const [loadingOptions, setLoadingOptions] = useState(false);
  
  // Contexto de autenticación
  const { currentUser, currentService, adminData } = useContext(AuthContext);

  // Cargar opciones de clientes al iniciar el componente
  useEffect(() => {
    const loadClientes = async () => {
      if (!currentService?.id) return;
      
      setLoadingOptions(true);
      try {
        // Pasar los IDs de clientes permitidos desde adminData
        const clientesList = await fetchClientes(
          currentService.id, 
          adminData?.clientes || []
        );
        setClientes(clientesList);
      } catch (error) {
        console.error("Error al cargar los clientes:", error);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadClientes();
  }, [currentService?.id, adminData?.clientes]);

  // Cargar opciones de servicios cuando cambian los clientes seleccionados
  useEffect(() => {
    const loadServicios = async () => {
      if (!currentService?.id || clienteFilter.length === 0) {
        setServicios([]);
        return;
      }
      
      setLoadingOptions(true);
      try {
        // Cargar servicios para cada cliente seleccionado, pasando tiendas permitidas
        const serviciosPromises = clienteFilter.map(cliente => 
          fetchTiendasPorCliente(
            currentService.id, 
            cliente, 
            adminData?.tiendas || []
          )
        );
        
        const resultados = await Promise.all(serviciosPromises);
        
        // Combinar y eliminar duplicados
        const combinedServicios = resultados.flat();
        const uniqueServicios = Array.from(
          new Map(combinedServicios.map(item => [item.numero, item])).values()
        );
        
        setServicios(uniqueServicios);
        
        // Almacenar todos los servicios para uso futuro
        if (todosServicios.length === 0) {
          setTodosServicios(uniqueServicios);
        }
      } catch (error) {
        console.error("Error al cargar los servicios:", error);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadServicios();
  }, [currentService?.id, clienteFilter, adminData?.tiendas]);

  // Cargar todos los servicios disponibles si no hay clientes seleccionados
  useEffect(() => {
    const loadAllServicios = async () => {
      if (!currentService?.id) return;
      
      if (clienteFilter.length === 0 && todosServicios.length === 0) {
        setLoadingOptions(true);
        try {
          // Buscar todas las tiendas/servicios permitidos para este usuario
          const allClientes = await fetchClientes(
            currentService.id, 
            adminData?.clientes || []
          );
          
          const serviciosPromises = allClientes.map(cliente => 
            fetchTiendasPorCliente(
              currentService.id, 
              cliente.nombre, 
              adminData?.tiendas || []
            )
          );
          
          const resultados = await Promise.all(serviciosPromises);
          const allServicios = resultados.flat();
          
          // Eliminar duplicados
          const uniqueServicios = Array.from(
            new Map(allServicios.map(item => [item.numero, item])).values()
          );
          
          setTodosServicios(uniqueServicios);
          setServicios(uniqueServicios);
        } catch (error) {
          console.error("Error al cargar todos los servicios:", error);
        } finally {
          setLoadingOptions(false);
        }
      }
    };
    
    loadAllServicios();
  }, [currentService?.id, clienteFilter, todosServicios.length, adminData?.clientes, adminData?.tiendas]);

  // Filtrar clientes por búsqueda
  const filteredClientes = clientes.filter(cliente => 
    cliente.nombre.toLowerCase().includes(clienteSearch.toLowerCase())
  );

  // Filtrar servicios por búsqueda
  const filteredServicios = servicios.filter(servicio => {
    const searchTerms = servicioSearch.toLowerCase();
    return (
      servicio.nombre.toLowerCase().includes(searchTerms) ||
      servicio.numero.toLowerCase().includes(searchTerms)
    );
  });

  // Manejar cambio en la selección de clientes
  const handleClienteChange = (event, newValue) => {
    setClienteFilter(newValue);
    // Si no hay clientes seleccionados, mostrar todos los servicios
    if (newValue.length === 0) {
      setServicios(todosServicios);
    }
  };

  // Manejar cambio en la selección de servicios
  const handleServicioChange = (event, newValue) => {
    setServicioFilter(newValue);
  };

  // Aplicar filtros
  const handleApplyFilters = () => {
    onFilterApply({
      clientes: clienteFilter,
      servicios: servicioFilter.map(s => s.numero)
    });
  };

  // Limpiar filtros
  const handleClearFilters = () => {
    setClienteFilter([]);
    setServicioFilter([]);
    setClienteSearch('');
    setServicioSearch('');
    onFilterApply({
      clientes: [],
      servicios: []
    });
  };

  return (
    <div className="vigilante-filters">
      <div className="vigilante-filters-title">
        <FilterListIcon />
        Filtrar usuarios
      </div>
      
      <div className="filters-row">
        {/* Selector múltiple de clientes con búsqueda */}
        <div className="filter-item">
          <Autocomplete
            multiple
            id="cliente-filter"
            options={filteredClientes.map(c => c.nombre)}
            value={clienteFilter}
            onChange={handleClienteChange}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip 
                  label={option} 
                  {...getTagProps({ index })} 
                  color="primary" 
                  variant="outlined"
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Clientes"
                placeholder="Buscar clientes..."
                onChange={(e) => setClienteSearch(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  )
                }}
              />
            )}
            disabled={loadingOptions || isLoading}
          />
        </div>

        {/* Selector múltiple de servicios con búsqueda */}
        <div className="filter-item">
          <Autocomplete
            multiple
            id="servicio-filter"
            options={filteredServicios}
            value={servicioFilter}
            onChange={handleServicioChange}
            getOptionLabel={(option) => `${option.nombre} (${option.numero})`}
            isOptionEqualToValue={(option, value) => option.numero === value.numero}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip 
                  label={`${option.nombre} (${option.numero})`} 
                  {...getTagProps({ index })} 
                  color="primary" 
                  variant="outlined"
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Servicios"
                placeholder="Buscar servicios..."
                onChange={(e) => setServicioSearch(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  )
                }}
              />
            )}
            disabled={loadingOptions || isLoading}
          />
        </div>
      </div>

      <div className="filter-actions">
        <button
          className="clear-button"
          onClick={handleClearFilters}
          disabled={isLoading || loadingOptions}
        >
          <ClearIcon />
          Limpiar
        </button>
        
        <button
          className="apply-button"
          onClick={handleApplyFilters}
          disabled={isLoading || loadingOptions}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} className="loading-spinner" />
              Cargando...
            </>
          ) : (
            <>
              <SearchIcon />
              Aplicar filtros
            </>
          )}
        </button>
      </div>
      
      {loadingOptions && (
        <div className="loading-indicator">
          <CircularProgress size={24} className="loading-spinner" />
          <span>Cargando opciones...</span>
        </div>
      )}
    </div>
  );
};

export default VigilanteFilters;